import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { MarkEmailRead } from "@mui/icons-material";
import { authApi } from "../../services/api/auth.api";
import { consumeReturnUrl, isSafeInternalPath } from "../../utils/return-url";

const wrapperSx = {
  p: 2,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "calc(100vh - 64px)",
} as const;

const cardSx = {
  maxWidth: 480,
  width: "100%",
  p: 4,
  boxShadow: (theme: Theme) => theme.shadows[3],
} as const;

/**
 * Извлекает email из location.state без доверия к форме (state — внешние
 * по отношению к компоненту данные: их мог выставить любой navigate).
 */
function extractEmailFromState(state: unknown): string | null {
  if (
    typeof state === "object" &&
    state !== null &&
    "email" in state &&
    typeof (state as { email: unknown }).email === "string" &&
    (state as { email: string }).email.length > 0
  ) {
    return (state as { email: string }).email;
  }
  return null;
}

/**
 * Извлекает путь возврата из location.state.from (пишет RegisterForm,
 * донося state.from от ProtectedRoute через ветку регистрации). Принимается
 * только внутренний путь приложения (анти open-redirect).
 */
function extractFromState(state: unknown): string | null {
  if (
    typeof state === "object" &&
    state !== null &&
    "from" in state &&
    typeof (state as { from: unknown }).from === "string" &&
    isSafeInternalPath((state as { from: string }).from)
  ) {
    return (state as { from: string }).from;
  }
  return null;
}

/**
 * Страница-уведомление после успешной регистрации.
 *
 * Флоу: RegisterForm после СВЕЖЕЙ регистрации ведёт сюда с `state.email`
 * (авто-логин сохраняется — пользователь уже аутентифицирован). Страница
 * объясняет, что на почту отправлено письмо подтверждения и что verify
 * нужен для создания событий; даёт «Отправить повторно» и переход в приложение.
 *
 * Прямой заход без email в state (ссылка руками) → редирект на /auth/register:
 * без email страница бессмысленна (resend не к чему привязать).
 */
const RegistrationSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = extractEmailFromState(location.state);
  const from = extractFromState(location.state);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  if (!email) {
    return <Navigate to="/auth/register" replace />;
  }

  // Приоритет возврата: state.from (ветка «вход → регистрация» доносит место) →
  // sessionStorage.returnUrl (гость с публичной страницы события; одноразовое
  // потребление ТОЛЬКО по клику — чтобы рендер не съедал ключ) → /dashboard.
  const handleContinue = () => {
    navigate(from ?? consumeReturnUrl() ?? "/dashboard");
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await authApi.resendVerification(email);
    } catch {
      // Анти-enumeration: не раскрываем результат (существует ли аккаунт) — тот же
      // «тихий успех», что и на BE. Ошибку не показываем.
    } finally {
      setResendDone(true);
      setResendLoading(false);
    }
  };

  return (
    <Box sx={wrapperSx}>
      <Paper sx={cardSx}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <MarkEmailRead color="primary" sx={{ fontSize: 48 }} />
        </Box>

        <Typography variant="h4" align="center" gutterBottom>
          Проверьте почту
        </Typography>

        <Alert severity="success" sx={{ mb: 3 }}>
          Аккаунт создан. Мы отправили письмо с ссылкой подтверждения на{" "}
          <strong>{email}</strong>.
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Подтверждение email обязательно для создания событий. Если письмо не
          пришло — проверьте папку «Спам» или отправьте его повторно.
        </Typography>

        {resendDone && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Если аккаунт с этим email существует и ещё не подтверждён — новое
            письмо отправлено. Проверьте почту.
          </Alert>
        )}

        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={handleResend}
          disabled={resendLoading || resendDone}
          sx={{ mb: 2 }}
        >
          {resendLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Отправить повторно"
          )}
        </Button>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleContinue}
        >
          Продолжить в приложение
        </Button>
      </Paper>
    </Box>
  );
};

export default RegistrationSuccessPage;
